import React, { useState, useEffect, useContext } from "react";
import { useGetRecentOrdersQuery } from "../api/api";
import SkeletonTableLoading from "../skeletonLoading/SkeletonTableLoading";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button } from "../@/components/ui/button";
import { setAppState } from "../redux/slices/appState";
import { NotificationContext } from "./ProtectedRoute";

interface OrderItem {
  price: number;
  quantity: number;
  defaultImageUrl: string;
  productName: string;
  id: number;
  statusName: string;
  createdAt: string;
  orderId: string;
}

interface Orders {
  orderId: number;
  statusId: number;
  statusName: string;
  createdAt: string;
  orderItems: OrderItem[];
}

const RecentOrders: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    data: recentOrders,
    error,
    isLoading,
    refetch: refetchRecentOrders,
  } = useGetRecentOrdersQuery();

  const [recentItems, setRecentItems] = useState<OrderItem[]>([]);

  useEffect(() => {
    if (recentOrders && Array.isArray(recentOrders.data)) {
      const allItems = recentOrders.data.reduce((acc, order) => {
        const itemsWithOrderInfo = order.orderItems.map((item: any) => ({
          ...item,
          createdAt: order.createdAt,
          statusName: order.statusName,
          orderId: order.orderId,
        }));
        return [...acc, ...itemsWithOrderInfo];
      }, []);

      const firstTenItems = allItems.slice(0, 10);
      setRecentItems(firstTenItems);
    }
  }, [recentOrders]);

  const statusClasses: { [key in Orders["statusName"]]: string } = {
    Drafted:
      "bg-processingStatusBgColor text-processingStatusTextColor px-[1.4vw]",
    Activated: "bg-green-200 text-green-700",
    Cancelled:
      "bg-cancelledStatusBgColor text-cancelledStatusTextColor px-[0.8vw]",
    Delivered: "bg-deliveredStatusBgColor text-deliveredStatusTextColor",
    Completed: "bg-deliveredStatusBgColor text-deliveredStatusTextColor",
    Confirmed: "bg-yellow-200 text-yellow-700 px-[0.7vw]",
    Returned: "bg-gray-200 text-gray-700",
    Dispatched: "bg-blue-200 text-blue-700 px-[0.5vw]",
  };
  const notifications = useContext(NotificationContext);
  useEffect(() => {
    refetchRecentOrders();
  }, [notifications]);
  return (
    <div className="bg-white p-2 w-full rounded-lg shadow-lg min-h-[84vh] max-h-[84vh] xxl:min-h-[76vh] xxl:max-h-[76vh] overflow-y-auto md:mx-auto m-0">
      <div className="w-full">
        <div className="flex justify-between items-center border-b-customBgGray">
          <h2 className="text-lg text-customGrayDark font-semibold ">
            Recent Orders
          </h2>
          <Button
            variant="outline"
            className="inline h-8 w-auto text-sm font-semibold border-none hover:bg-transparent text-dHuntGreen bg-transparent hover:underline hidden md:flex"
            onClick={() => {
              dispatch(setAppState({ routeName: "orders" }));
              navigate("/orders");
            }}
          >
            View All
          </Button>
        </div>
        <ul>
          {isLoading ? (
            <div className="h-full w-full">
              <SkeletonTableLoading />
            </div>
          ) : recentItems.length == 0 ? (
            <div className="flex items-center mt-5 justify-center ">
              No Recent Orders found.
            </div>
          ) : (
            recentItems.map((order, index) => (
              <li
                onClick={() => {
                  navigate(`/edit-order/${order.orderId}`);
                }}
                key={index}
                className="flex items-center justify-between h-full cursor-pointer py-4 border-b border-gray-200 last:border-b-0 hover:bg-gray-50"
              >
                <div className="flex items-center space-x-4">
                  <img
                    src={order.defaultImageUrl}
                    alt={order.productName}
                    className="w-10 h-10 object-cover rounded"
                  />
                  <div className="text-xs font-helveticaMedium text-customBlackColor">
                    <div>{order.productName}</div>
                    <div>
                      ${order.price.toFixed(2)} x {order.quantity}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-gray-400 text-xs mb-1">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </div>
                  <span
                    className={`px-3 py-1 rounded-lg text-xs font-normal ${
                      statusClasses[order.statusName]
                    }`}
                  >
                    {order.statusName}
                  </span>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
};

export default RecentOrders;
